<script setup lang="ts">
import {
  CsvExtension,
  GenericExtension,
  ImageExtension,
  ModelExtension,
  PdfExtension,
  Upload,
  VideoExtension,
} from '@/generated/graphql'
import { computed } from 'vue'

type PreviewableBase = Pick<Upload, 'name'>
type PreviewableCsv = PreviewableBase & {
  extension: Pick<CsvExtension, '__typename'>
}
type PreviewableGeneric = PreviewableBase & {
  extension: Pick<GenericExtension, '__typename'>
}
type PreviewableModel = PreviewableBase & {
  extension: Pick<ModelExtension, '__typename'>
}
type PreviewableImage = PreviewableBase & {
  extension: Pick<ImageExtension, 'url' | '__typename'>
}
type PreviewablePdf = PreviewableBase & {
  extension: Pick<PdfExtension, '__typename'>
}
type PreviewableVideo = PreviewableBase & {
  extension: Pick<VideoExtension, '__typename'> & Partial<Pick<VideoExtension, 'url'>>
}
export type PreviewableUpload =
  | PreviewableCsv
  | PreviewableGeneric
  | PreviewableModel
  | PreviewableImage
  | PreviewablePdf
  | PreviewableVideo

const props = defineProps<{
  upload: PreviewableUpload
  iconSize?: string
  iconOnly?: boolean
  videoTimeInMillis?: number
}>()
const type = computed(() => props.upload.extension.__typename)
const image = computed(() =>
  !props.iconOnly && type.value == 'ImageExtension'
    ? (props.upload as PreviewableImage)
    : undefined,
)
const video = computed(() => {
  if (props.iconOnly || type.value != 'VideoExtension') {
    return undefined
  }

  const upload = props.upload as PreviewableVideo
  return upload.extension.url ? upload : undefined
})
// The media fragment makes the browser paint the frame at that time as the thumbnail.
const videoSrc = computed(
  () => `${video.value?.extension.url}#t=${(props.videoTimeInMillis || 100) / 1000}`,
)
const icon = computed(() => {
  switch (props.upload.extension.__typename) {
    case 'ImageExtension':
      return 'image'
    case 'VideoExtension':
      return 'movie'
    case 'CsvExtension':
      return 'grid_on'
    case 'ModelExtension':
      return 'view_in_ar'
    default:
      return 'description'
  }
})
</script>

<template>
  <div class="text-center align-content-center" style="height: 100%">
    <v-img
      v-if="!!image"
      v-bind="$attrs"
      :src="image.extension.url"
      :title="image.name"
      class="mx-auto"
    />
    <span v-else-if="!!video" class="video-thumb">
      <video
        v-bind="$attrs"
        :src="videoSrc"
        :title="video.name"
        crossorigin="anonymous"
        preload="metadata"
        muted
      />
      <v-icon class="video-thumb-icon" :size="props.iconSize">play_circle</v-icon>
    </span>
    <v-icon v-else :size="props.iconSize">
      {{ icon }}
    </v-icon>
  </div>
</template>

<style scoped lang="scss">
.video-thumb {
  position: relative;
  display: inline-block;
  max-width: 100%;
  height: 100%;

  video {
    max-width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }
}
.video-thumb-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.6));
}
</style>
